import Dialog from "@mui/material/Dialog";
import { type FormEvent, useId, useState } from "react";
import { GfgButton } from "@/components/ui/GfgButton";
import { GfgTextField } from "@/components/ui/GfgTextField";
import { BOUNCES_TEXT } from "@/constants/reportsBounces";
import { useResendBounce } from "@/hooks/useReportsBounces";
import type { BounceRow } from "@/interfaces/reportsBounces";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { bounceSubtitle, isValidNewEmail, newEmailError } from "@/utils/reportsBounces-helper";
import { orDash } from "@/utils/reportsRedemptions-helper";

export interface FixBouncedEmailModalProps {
  /** The row being fixed. The panel mounts this component only while its row is the open one, so
   *  `row` never switches underneath an edit in progress — closing unmounts it outright. */
  row: BounceRow;
  onClose: () => void;
  /** Called after the API confirmed the resend, so the panel can mark the row and toast. */
  onResent: (row: BounceRow, newEmail: string) => void;
}

/**
 * The Fix bounced email form (GFG-271). Its own MUI Dialog rather than GfgModal, same as
 * `admin/sendQueueCampaigns/RescheduleModal.tsx`: this is a form, not a yes/no confirm. MUI covers
 * the behaviour primitives (focus trap, portal, Escape/backdrop, scroll lock); the appearance is
 * Tailwind + design tokens.
 *
 * Unlike RescheduleModal this component owns the mutation, because a failed resend has to be
 * reported inside the still-open dialog with the address the user typed still in the field — a
 * caller-owned mutation would have to close the dialog to show the error anywhere.
 *
 * No permission re-check here: the Action column is what decides whether Fix exists at all, and
 * the server is the real boundary.
 */
export default function FixBouncedEmailModal({
  row,
  onClose,
  onResent,
}: FixBouncedEmailModalProps) {
  const [draft, setDraft] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const resend = useResendBounce();

  const titleId = useId();
  const subtitleId = useId();
  const newEmailId = useId();

  const validationError = newEmailError(draft, row.recipientEmail);
  const canResend = isValidNewEmail(draft, row.recipientEmail) && !resend.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canResend) return;

    const newEmail = draft.trim();
    setFailure(null);
    resend.mutate(
      { contactId: row.id, newEmail },
      {
        onSuccess: () => onResent(row, newEmail),
        // Stay open and re-enable: the address the user typed is the thing they need to correct.
        onError: (error) => setFailure(apiErrorMessage(error, BOUNCES_TEXT.modal.error)),
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        // Focus has to land after the open transition: the Dialog's FocusTrap pulls focus onto the
        // paper once it entered, overwriting the mount-time `.focus()` React runs for `autoFocus`.
        transition: { onEntered: () => document.getElementById(newEmailId)?.focus() },
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            m: 2,
            width: 440,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      {/* noValidate: our own `newEmailError` owns the message, so the browser's bubble never
          competes with the sentence shown under the field. */}
      <form onSubmit={handleSubmit} noValidate>
        <h2
          id={titleId}
          className="mb-1.5 text-left font-crimson font-normal text-2xl text-rodeo-black"
        >
          {BOUNCES_TEXT.modal.title}
        </h2>
        <p id={subtitleId} className="mb-5 font-lato text-graphite text-sm">
          {bounceSubtitle(row)}
        </p>

        <div className="mb-5 rounded-lg border border-smoke bg-wax px-3.5 py-3">
          <p className="mb-1 font-bold font-lato text-[11px] text-graphite uppercase tracking-[1.5px]">
            {BOUNCES_TEXT.modal.bouncedLabel}
          </p>
          <p className="font-lato text-rodeo-black text-sm">{orDash(row.recipientEmail)}</p>
        </div>

        <GfgTextField
          id={newEmailId}
          label={BOUNCES_TEXT.modal.newEmailLabel}
          type="email"
          autoComplete="email"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            // The server failure describes the address that was just rejected; editing invalidates
            // it, so it must not sit next to the field's own validation message.
            setFailure(null);
          }}
          error={validationError !== null}
          errorText={validationError ?? undefined}
        />

        {failure && (
          <p role="alert" className="mt-3 font-lato text-good-wine text-xs">
            {failure}
          </p>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <GfgButton type="button" variant="secondary" onClick={onClose}>
            {BOUNCES_TEXT.modal.cancel}
          </GfgButton>
          <GfgButton type="submit" variant="primary" disabled={!canResend}>
            {BOUNCES_TEXT.modal.resend}
          </GfgButton>
        </div>
      </form>
    </Dialog>
  );
}
